/** @jsxRuntime classic */
/** @jsx jsx */

import { useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors, Typography } from '@zapier/design-system';
import { id } from '../../../utils/id';
import { BREAKPOINTS, COLOR_KEYS, MEDIA_MIN_WIDTH } from '../../../constants';
import { SectionName, useAvroTracking } from '../../../observability';
import { CustomButton } from '../../CustomButton';
import { RichTextHeading } from '../../RichTextHeading';
import { RichTextHTMLString } from '../types';
import { AccordionItem, AccordionItemProps } from './AccordionItem';

export interface SplitColorAccordionSectionProps {
  accordionItems: Pick<
    AccordionItemProps,
    | 'animationAssetsUrl'
    | 'animationDataUrl'
    | 'body'
    | 'imageUrl'
    | 'heading'
    | 'mediaAltText'
  >[];
  backgroundColor:
    | typeof COLOR_KEYS.bolt
    | typeof COLOR_KEYS.moss
    | typeof COLOR_KEYS.neutral800
    | typeof COLOR_KEYS.night
    | typeof COLOR_KEYS.peach
    | typeof COLOR_KEYS.zapier;
  ctaText: string;
  ctaUrl: string;
  heading: RichTextHTMLString;
}

const Styles = labelStyles('SplitColorAccordionSection', {
  root: (backgroundColor: SplitColorAccordionSectionProps['backgroundColor']) =>
    css({
      position: 'relative',
      maxWidth: '2048px',
      margin: '0 auto',

      [MEDIA_MIN_WIDTH.small]: {
        backgroundColor: Colors[backgroundColor],
      },
    }),

  contentContainer: css({
    padding: '60px 20px',
    boxSizing: 'border-box',
    backgroundColor: Colors.neutral100,

    [MEDIA_MIN_WIDTH.small]: {
      width: '45%',
      padding: '48px 20px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      padding: '96px 0 96px max(calc((min(2048px, 100vw) - 1170px) / 2), 20px)',
    },

    [MEDIA_MIN_WIDTH.extraLarge]: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    },
  }),

  content: css({
    height: '100%',

    [MEDIA_MIN_WIDTH.small]: {
      maxWidth: '320px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '370px',
    },

    [MEDIA_MIN_WIDTH.extraLarge]: {
      flex: 1,
    },
  }),

  heading: css({
    ...Typography.pageHeader7,
    color: Colors.earth,
    marginBottom: '16px',
    flexShrink: 0,

    [MEDIA_MIN_WIDTH.small]: {
      minHeight: '120px',
      marginBottom: '48px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.pageHeader5,
    },
  }),

  accordion: css({
    marginBottom: '24px',
  }),
});

export function SplitColorAccordionSection(
  props: SplitColorAccordionSectionProps,
) {
  const { emitCTAClickEvent } = useAvroTracking();
  const rootRef = useRef<HTMLDivElement>(null);
  const [sectionId] = useState(id('section-'));
  const [activeAccordionItem, setActiveAccordionItem] = useState(0);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const root = rootRef.current;

    if (!root) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      { threshold: 0.25 },
    );

    observer.observe(root);

    return () => observer.unobserve(root);
  }, []);

  /**
   * If all accordion items are closed on a mobile-sized viewport and then the
   * window is resized to a desktop-sized viewport, the first accordion should
   * be opened as a fallback to prevent a weird state where no media is shown.
   * */
  useEffect(() => {
    const handleResize = () => {
      if (
        window.innerWidth >= BREAKPOINTS.small &&
        activeAccordionItem === -1
      ) {
        setActiveAccordionItem(0);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeAccordionItem]);

  const handleAccordionItemClick = (index: number) => {
    setActiveAccordionItem(
      index === activeAccordionItem && window.innerWidth < BREAKPOINTS.small
        ? -1
        : index,
    );
  };

  return (
    <div css={Styles.root(props.backgroundColor)} ref={rootRef}>
      <div css={Styles.contentContainer}>
        <div css={Styles.content}>
          <RichTextHeading css={Styles.heading} html={props.heading} tag="h2" />
          <div>
            <div css={Styles.accordion}>
              {props.accordionItems.map((item, index) => (
                <AccordionItem
                  {...item}
                  id={`${sectionId}-accordion-item-${index}`}
                  isActive={activeAccordionItem === index}
                  key={`${sectionId}-accordion-item-${index}`}
                  mediaBackgroundColor={props.backgroundColor}
                  onClick={() => handleAccordionItemClick(index)}
                  sectionIsIntersecting={isIntersecting}
                />
              ))}
            </div>
            <CustomButton
              href={props.ctaUrl}
              isFullWidth={true}
              onClick={() =>
                emitCTAClickEvent({
                  text: props.ctaText,
                  href: props.ctaUrl,
                  section: SectionName.SplitColorAccordionSection,
                })
              }
            >
              {props.ctaText}
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );
}
